import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import { getLinks } from 'pages/matters/util';
import LabelledData from 'pages/timecards/LabelledData';
import { FormattedMessage } from 'react-intl';
import { useGetPartyGroupByIdQuery } from 'store/api/groups';
import { ClassifierData } from 'types/classifiers';
import { getClassifierText, t } from 'utils/getTranslations';

const GroupInfoCard = ({
    groupId,
    classifiers,
}: {
    groupId: number;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => {
    const { data: groupDetails, isLoading } = useGetPartyGroupByIdQuery(String(groupId));

    if (isLoading || !groupDetails) {
        return <span className="text-caption">Loading…</span>;
    }

    const relationshipManagers = groupDetails.employees?.filter(
        (emp) => emp.groupEmployeeRoleTypeCode === 'RELATIONSHIP_MANAGER'
    );
    const relationshipPartners = groupDetails.employees?.filter(
        (emp) => emp.groupEmployeeRoleTypeCode === 'RELATIONSHIP_PARTNER'
    );

    return (
        <div>
            <span className="text-caption font-semibold">
                <FormattedMessage id="coi.rel-owners" />
            </span>
            <LabelledData label="coi.rel-partners">
                {relationshipPartners?.length
                    ? getLinks(
                          'employee',
                          relationshipPartners.map((emp) => emp.employee)
                      )
                    : '-'}
            </LabelledData>
            <LabelledData label="coi.rel-managers">
                {relationshipManagers?.length
                    ? getLinks(
                          'employee',
                          relationshipManagers.map((emp) => emp.employee)
                      )
                    : '-'}
            </LabelledData>
            <div className="border-divider mt-2 border-t pt-2">
                <LabelledData label="contacts.groups.segment">
                    {groupDetails.segmentTypeCode
                        ? getClassifierText(classifiers['SEGMENT_TYPE'], groupDetails.segmentTypeCode)
                        : '-'}
                </LabelledData>
                <LabelledData label="contacts.groups.service-level">
                    {groupDetails.serviceLevelTypeCode
                        ? getClassifierText(classifiers['SERVICE_LEVEL_TYPE'], groupDetails.serviceLevelTypeCode)
                        : '-'}
                </LabelledData>
            </div>
        </div>
    );
};

/**
 * A contact group's info icon, its relationship owners, segment and service level in the tooltip. A contact's read
 * view and its Contact groups field both draw it after the group's name, so a group's chip is as wide in both.
 */
export const GroupInfo = ({
    group,
    classifiers,
}: {
    group: { id: number; groupName: string };
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => (
    <Tooltip content={<GroupInfoCard groupId={group.id} classifiers={classifiers} />}>
        <span
            tabIndex={0}
            role="img"
            aria-label={`${t('command-palette.group-details')}: ${group.groupName}`}
            className="inline-flex"
        >
            <InfoOutlinedIcon className="text-grey-600 h-[1.2rem]" />
        </span>
    </Tooltip>
);
